/**
 * The camo a player already has, and what can be done to them.
 *
 * This is what makes the screen an editor rather than a generator: a camo is a
 * thing you keep, come back to, rename when the first name was wrong, and
 * delete when you stop liking it. Without a list, every save is a new record
 * and a collection quietly fills with near-duplicates nobody can tell apart.
 *
 * The list is read straight from the player's own PDS, which needs no account
 * and no token — see repo.ts for why reads and writes go different ways.
 * Signing in is the site's ordinary sign-in, and it is needed only to change
 * something.
 *
 * Destructive actions confirm in place rather than through `confirm()`: a
 * browser dialog is modal over the whole page, looks nothing like the site,
 * and on a delete of something small and remakeable it is more ceremony than
 * the action deserves. A second click on the same button is enough.
 */

import { currentSession, setDefaultCamo, type Session } from "../api";
import { el } from "../dom";
import type { StoredCamo } from "./repo";
import { CAMO_H, CAMO_W } from "./mech";

/** Loaded once, on the first action that needs an account. */
let repoPromise: Promise<typeof import("./repo")> | null = null;
export function repoModule(): Promise<typeof import("./repo")> {
  repoPromise ??= import("./repo");
  return repoPromise;
}

export type CollectionHooks = {
  /** Put this camo in the editor. */
  onOpen(camo: StoredCamo): void;
  /** Say something in the editor's own status line. */
  onStatus(message: string): void;
  /** Called when the signed-in account changes, including on first look. */
  onSession(session: Session | null): void;
  /**
   * Write the editor down before a sign-in navigates away. The provider sends
   * the player back to the site root, and without this the camo on screen is
   * gone by the time they return.
   */
  onBeforeConnect(): void;
};

export type Collection = {
  /** The panel to put on the page. */
  node: HTMLElement;
  /** Re-read the collection from the repository. */
  reload(): Promise<void>;
  /** Look for a session and, if there is one, list what it owns. */
  start(): Promise<Session | null>;
  /** The camo currently open in the editor, so Save knows what it is editing. */
  setOpen(camo: StoredCamo | null): void;
  /**
   * Put the site's sign-in dialog up. The panel's own Sign in button is one
   * way in; Save pressed with no account is the other, and both want the same
   * dialog and the same stashing of the editor before it navigates.
   */
  signIn(): void;
};

export function camoCollection(hooks: CollectionHooks): Collection {
  const list = el("div", { className: "camo-collection" });
  const account = el("div", { className: "camo-account" });
  const panel = el("div", { className: "camo-group camo-mine" }, [
    el("div", { className: "camo-group-head" }, [
      el("span", { className: "camo-label", textContent: "Your camos" }),
    ]),
    account,
    list,
  ]);

  let session: Session | null = null;
  let openRkey: string | null = null;
  /**
   * The camo matches launch with unless one is picked at the time. Held here
   * as well as on the session so the buttons can be re-marked without another
   * round trip; the session is still the thing that says what it was on load.
   */
  let defaultRkey: string | null = null;

  function setStatus(message: string): void {
    hooks.onStatus(message);
  }

  // --- account -------------------------------------------------------------

  function renderAccount(): void {
    account.replaceChildren();
    if (!session) {
      account.append(
        el("p", {
          className: "small muted",
          textContent:
            "Camo are saved to your own account, not to lance.blue. Sign in to keep them.",
        }),
        signInButton(),
      );
      return;
    }
    const who = session.handle ? `@${session.handle}` : session.did;
    account.append(
      el("p", { className: "small muted" }, [`Saving to ${who}.`]),
    );
  }

  function signInButton(): HTMLButtonElement {
    const button = el("button", {
      type: "button",
      className: "small-btn",
      textContent: "Sign in",
    });
    button.addEventListener("click", () => showSignIn());
    return button;
  }

  /**
   * The site's own sign-in dialog (signin-modal.ts), loaded on the click that
   * asks for it. The camo editor is one of two places that opens it; the
   * masthead is the other, and the wording of the grant belongs in one file.
   */
  function showSignIn(): void {
    void import("../signin-modal")
      .then((module) =>
        // Runs just before the browser leaves for the provider: once the
        // redirect fires this page is gone, along with whatever is on the
        // canvas.
        module.openSignIn({ onBeforeConnect: () => hooks.onBeforeConnect() }),
      )
      .catch((e: unknown) => {
        setStatus(failure(e, "The sign-in dialog did not load."));
      });
  }

  // --- the list ------------------------------------------------------------

  function card(camo: StoredCamo): HTMLElement {
    const canvas = el("canvas", {
      className: "camo-swatch camo-mine-swatch",
      width: CAMO_W,
      height: CAMO_H,
    });
    const name = el("span", {
      className: "camo-mine-name",
      textContent: camo.name,
    });

    const open = el("button", {
      type: "button",
      className: "secondary small-btn",
      textContent: "Open",
      title: camo.source
        ? "Load this camo back into the editor"
        : "Load this camo's picture; it was imported, so there are no settings to restore",
    });
    open.addEventListener("click", () => {
      openRkey = camo.rkey;
      markOpen();
      hooks.onOpen(camo);
    });

    const rename = el("button", {
      type: "button",
      className: "secondary small-btn",
      textContent: "Rename",
    });
    const remove = el("button", {
      type: "button",
      className: "secondary small-btn camo-danger",
      textContent: "Delete",
    });

    // The one camo a match reaches for when the player does not pick one on
    // the way in. A toggle rather than a one-way set, so the answer can be
    // "none of them" without deleting anything.
    const isDefault = () => defaultRkey === camo.rkey;
    const useByDefault = el("button", {
      type: "button",
      className: "secondary small-btn camo-default",
      title:
        "Start matches in this camo unless another is picked on the way in",
    });
    function markDefault(): void {
      useByDefault.textContent = isDefault() ? "Default ✓" : "Make default";
      useByDefault.classList.toggle("active", isDefault());
      useByDefault.setAttribute("aria-pressed", String(isDefault()));
    }
    markDefault();
    useByDefault.addEventListener("click", () => {
      const next = isDefault() ? null : camo.rkey;
      useByDefault.disabled = true;
      void setDefaultCamo(next)
        .then(() => {
          defaultRkey = next;
          // Every card, not just this one: setting a default unsets whatever
          // held it before, and that card is still on screen saying otherwise.
          renderList();
          setStatus(
            next
              ? `Matches will start in “${camo.name}”.`
              : "Matches will start in the default colours.",
          );
        })
        .catch((e: unknown) => {
          useByDefault.disabled = false;
          setStatus(failure(e, "That could not be saved."));
        });
    });

    const actions = el("div", { className: "camo-mine-actions" }, [
      open,
      useByDefault,
      rename,
      remove,
    ]);

    const item = el("div", { className: "camo-mine-item" }, [
      canvas,
      el("div", { className: "camo-mine-body" }, [name, actions]),
    ]);
    if (camo.rkey === openRkey) {
      item.classList.add("active");
      item.setAttribute("aria-current", "true");
    }

    rename.addEventListener("click", () => {
      const input = el("input", {
        type: "text",
        className: "camo-name",
        value: camo.name,
        maxLength: 64,
        "aria-label": "New name",
      });
      const confirm = el("button", {
        type: "button",
        className: "small-btn",
        textContent: "Save name",
      });
      const cancel = el("button", {
        type: "button",
        className: "secondary small-btn",
        textContent: "Cancel",
      });
      const editing = el("div", { className: "camo-mine-rename" }, [
        input,
        el("div", { className: "camo-mine-actions" }, [confirm, cancel]),
      ]);
      name.replaceWith(editing);
      actions.hidden = true;
      input.focus();
      input.select();

      const stop = () => {
        editing.replaceWith(name);
        actions.hidden = false;
      };
      cancel.addEventListener("click", stop);
      input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") stop();
        if (event.key === "Enter") confirm.click();
      });
      confirm.addEventListener("click", () => {
        const next = input.value.trim();
        if (!next) {
          input.focus();
          return;
        }
        if (next === camo.name) {
          stop();
          return;
        }
        confirm.disabled = true;
        setStatus(`Renaming “${camo.name}”…`);
        void repoModule()
          .then((repo) => repo.update(camo, { name: next }))
          .then(() => {
            setStatus(`Renamed to “${next}”.`);
            return reload();
          })
          .catch((e: unknown) => {
            confirm.disabled = false;
            setStatus(failure(e, "The camo could not be renamed."));
          });
      });
    });

    // Two clicks, not a browser dialog. The second click is the confirmation,
    // and anything else on the page puts the button back.
    let armed = false;
    const disarm = () => {
      armed = false;
      remove.textContent = "Delete";
      remove.classList.remove("armed");
    };
    remove.addEventListener("click", () => {
      if (!armed) {
        armed = true;
        remove.textContent = "Really delete?";
        remove.classList.add("armed");
        setTimeout(() =>
          document.addEventListener("click", disarm, { once: true }),
        );
        return;
      }
      disarm();
      remove.disabled = true;
      setStatus(`Deleting “${camo.name}”…`);
      void repoModule()
        .then((repo) => repo.remove(camo))
        .then(() => {
          if (openRkey === camo.rkey) {
            openRkey = null;
            hooks.onOpen({ ...camo, rkey: "" });
          }
          setStatus(`Deleted “${camo.name}”.`);
          return reload();
        })
        .catch((e: unknown) => {
          remove.disabled = false;
          setStatus(failure(e, "The camo could not be deleted."));
        });
    });

    // The picture last: the card is usable before the bytes arrive. The URL
    // is the blob on the player's own PDS, which serves it to anyone.
    if (camo.imageUrl) {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => canvas.getContext("2d")?.drawImage(image, 0, 0);
      image.onerror = () =>
        canvas.replaceWith(
          el("span", { className: "camo-mine-noimage", textContent: "?" }),
        );
      image.src = camo.imageUrl;
    }

    return item;
  }

  function markOpen(): void {
    for (const item of list.querySelectorAll(".camo-mine-item")) {
      item.classList.remove("active");
      item.removeAttribute("aria-current");
    }
    if (!openRkey) return;
    const index = [...list.children].findIndex(
      (child) => child.getAttribute("data-rkey") === openRkey,
    );
    const active = list.children[index];
    active?.classList.add("active");
    active?.setAttribute("aria-current", "true");
  }

  /**
   * The last collection read, so the list can be redrawn without asking the
   * repository again. Marking a default changes two cards and no records.
   */
  let listed: StoredCamo[] = [];

  function renderList(): void {
    list.replaceChildren();
    if (!listed.length) {
      list.append(
        el("p", {
          className: "small muted",
          textContent: "No camo saved yet. Name one and press Save.",
        }),
      );
      return;
    }
    for (const camo of listed) {
      const item = card(camo);
      item.setAttribute("data-rkey", camo.rkey);
      list.append(item);
    }
  }

  async function reload(): Promise<void> {
    if (!session) return;
    try {
      const repo = await repoModule();
      listed = await repo.list(session.did);
      renderList();
    } catch (e: unknown) {
      list.replaceChildren(
        el("p", {
          className: "error",
          role: "alert",
          textContent: failure(e, "Your camo could not be listed."),
        }),
      );
    }
  }

  async function start(): Promise<Session | null> {
    try {
      session = await currentSession();
    } catch {
      // Signed out is the ordinary answer, and so is an API that is not
      // running. Neither stops anyone designing a camo.
      session = null;
    }
    defaultRkey = session?.defaultCamo ?? null;
    renderAccount();
    hooks.onSession(session);
    if (session) await reload();
    return session;
  }

  renderAccount();

  return {
    node: panel,
    reload,
    start,
    setOpen(camo) {
      openRkey = camo?.rkey ?? null;
      markOpen();
    },
    signIn: showSignIn,
  };
}

/** A message for the player, preferring the one the failure carried. */
export function failure(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
